/**
 * Derive the set of workflow states an instance ACTUALLY visited, from its
 * action-log history.
 *
 * A progress rail must not infer "completed" from a state's ordinal position
 * in the workflow definition. A claim can take a shortcut - e.g. a
 * ``bulk_settle`` action that jumps straight from "Awaiting Documents" to
 * "Compensated" - and never enter the states ordered in between (Intake
 * Review, Lab Analysis, Material & Labor Review, ...). Painting those earlier
 * states as "done" is a lie: the workflow engine's own reopen resolver reads
 * the real action-log history and knows the instance never visited them. The
 * UI must read the same source of truth so the two never disagree.
 *
 * A state counts as visited when:
 *   - it is the instance's current state (you are in it now), or
 *   - it is the workflow's initial state (always entered on creation), or
 *   - a successful (``result === 'OK'``) action-log entry transitioned INTO
 *     it (``to_state``), or recorded it as the state left (``from_state`` -
 *     the instance must have been sitting in a state to leave it).
 *
 * Failed / dry-run / pending attempts are ignored: the engine writes a
 * provisional ``to_state`` before it knows the transition will commit, so a
 * ``FAILED`` log can carry a target the instance never actually entered.
 * Entries with no ``result`` field (older/leaner payloads that only surface
 * genuine transitions) are trusted as-is.
 *
 * Generic over the log shape so it serves every history payload in the app:
 * the generic ``WorkflowActionLog`` (flat ``to_state_key`` /
 * ``from_state_key``), the admin/consumer claim history (nested
 * ``to_state`` / ``from_state`` objects), etc. Pass whichever you have.
 */

interface StateRef {
  key?: string | null
}

/** Minimal shape the visited-set derivation reads off a history entry. */
export interface VisitedHistoryEntry {
  /** Flat key (``WorkflowActionLog``) or nested ref ({ key }) - both handled. */
  to_state?: string | StateRef | null
  to_state_key?: string | null
  from_state?: string | StateRef | null
  from_state_key?: string | null
  /** Transition result; when present, only ``'OK'`` entries count as visits. */
  result?: string | null
}

interface InitialLike {
  key: string
  is_initial?: boolean
}

export interface VisitedStateOptions {
  /**
   * The workflow's ordered states, used to locate the initial state when
   * ``initialStateKey`` is not supplied.
   */
  states?: readonly InitialLike[]
  /** The instance's current state key (always counts as visited). */
  currentStateKey?: string | null
  /** Explicit initial state key (takes precedence over ``states``). */
  initialStateKey?: string | null
}

function refKey(ref: string | StateRef | null | undefined): string | null {
  if (!ref) return null
  if (typeof ref === 'string') return ref || null
  return ref.key || null
}

export function computeVisitedStateKeys(
  history: readonly VisitedHistoryEntry[] | undefined | null,
  options: VisitedStateOptions = {},
): Set<string> {
  const visited = new Set<string>()

  const initialKey =
    options.initialStateKey ??
    options.states?.find((s) => s.is_initial)?.key ??
    null
  if (initialKey) visited.add(initialKey)
  if (options.currentStateKey) visited.add(options.currentStateKey)

  for (const entry of history ?? []) {
    // A failed / dry-run / pending attempt did not move the instance, but the
    // engine may have written a provisional target on it - ignore those.
    if (entry.result && entry.result !== 'OK') continue
    const to = refKey(entry.to_state) ?? entry.to_state_key ?? null
    if (to) visited.add(to)
    const from = refKey(entry.from_state) ?? entry.from_state_key ?? null
    if (from) visited.add(from)
  }

  return visited
}
